import * as pc from "playcanvas";
import { MOVEMENT_FPS, getArenaAABBs, rayHitAabb } from "@hupper/shared";
import { buildMolotov } from "../player/molotovDesign";

/**
 * GrenadeProjectile — locally-simulated ballistic projectile.
 *
 * Physics: gravity-only, with a single bounce off the floor (damping
 * 0.35, lateral friction 0.6). When the fuse runs out OR the projectile
 * comes (nearly) to rest on the ground, calls onLand with the final
 * position. The caller spawns the matching effect.
 *
 * Simple by design — no wall collisions, no per-tick networking. Good
 * enough for the player to feel the throw arc.
 */
export interface ProjectileSpec {
  origin: pc.Vec3;
  velocity: pc.Vec3;
  /** Seconds before the grenade is "done" regardless of motion (HE/flash). */
  fuseSec: number;
  /** Fire effect immediately on first floor contact (molotov). */
  detonateOnFloor: boolean;
}

const GRAVITY = 14.5; // m/s² (tweaked higher than 9.81 for snappier feel)
const FLOOR = MOVEMENT_FPS.FLOOR_Y;

export class GrenadeProjectile {
  private readonly app: pc.AppBase;
  private readonly entity: pc.Entity;
  private readonly velocity: pc.Vec3;
  private readonly spec: ProjectileSpec;
  private readonly onLand: (pos: pc.Vec3) => void;
  private age = 0;
  private settled = false;
  private done = false;
  private readonly tickFn: (dt: number) => void;

  constructor(
    app: pc.AppBase,
    spec: ProjectileSpec,
    onLand: (pos: pc.Vec3) => void,
    color: pc.Color,
  ) {
    this.app = app;
    this.spec = spec;
    this.onLand = onLand;
    this.velocity = spec.velocity.clone();

    this.entity = new pc.Entity("grenade-projectile");
    
    // Create the appropriate procedural model based on color (hacky way to tell which grenade it is, but it works for now)
    // HE: (0.32, 0.42, 0.18)
    // Flash: (0.85, 0.78, 0.72)
    // Smoke: (0.55, 0.55, 0.58)
    // Molotov: (0.85, 0.32, 0.12)
    
    const mat = new pc.StandardMaterial();
    mat.diffuse = color;
    mat.gloss = 0.4;
    mat.update();

    const steel = new pc.StandardMaterial();
    steel.diffuse = new pc.Color(0.38, 0.39, 0.42);
    steel.gloss = 0.55;
    steel.update();

    const dark = new pc.StandardMaterial();
    dark.diffuse = new pc.Color(0.12, 0.12, 0.13);
    dark.gloss = 0.25;
    dark.update();

    const addPart = (parent: pc.Entity, type: "box" | "cylinder" | "capsule" | "sphere", name: string, scale: [number, number, number], pos: [number, number, number], euler: [number, number, number], material: pc.StandardMaterial) => {
      const e = new pc.Entity(name);
      e.addComponent("render", { type });
      e.setLocalScale(scale[0], scale[1], scale[2]);
      e.setLocalPosition(pos[0], pos[1], pos[2]);
      e.setLocalEulerAngles(euler[0], euler[1], euler[2]);
      const r = e.render;
      if (r) { r.material = material; r.castShadows = false; r.receiveShadows = false; }
      parent.addChild(e);
      return e;
    };
    // `mat`-shaped helper so the shared molotov builder can mint its own
    // opaque materials (the local `mat` above is a single coloured instance).
    const makeMat = (r: number, g: number, b: number, gloss = 0.35) => {
      const m = new pc.StandardMaterial();
      m.diffuse = new pc.Color(r, g, b);
      m.gloss = gloss;
      m.update();
      return m;
    };

    if (color.r === 0.55 && color.g === 0.55) {
      // Smoke
      const stripe = new pc.StandardMaterial(); stripe.diffuse = new pc.Color(0.2, 0.8, 0.2); stripe.update();
      addPart(this.entity, "cylinder", "smoke-body", [0.06, 0.12, 0.06], [0, -0.02, 0], [0, 0, 0], mat);
      addPart(this.entity, "cylinder", "smoke-stripe", [0.062, 0.02, 0.062], [0, 0.02, 0], [0, 0, 0], stripe);
      addPart(this.entity, "cylinder", "smoke-cap", [0.06, 0.01, 0.06], [0, 0.045, 0], [0, 0, 0], dark);
      addPart(this.entity, "cylinder", "smoke-neck", [0.02, 0.02, 0.02], [0, 0.06, 0], [0, 0, 0], steel);
    } else if (color.r === 0.85 && color.g === 0.78) {
      // Flash
      const stripe = new pc.StandardMaterial(); stripe.diffuse = new pc.Color(0.2, 0.4, 0.9); stripe.update();
      addPart(this.entity, "cylinder", "flash-body", [0.05, 0.11, 0.05], [0, -0.02, 0], [0, 0, 0], mat);
      addPart(this.entity, "cylinder", "flash-stripe", [0.052, 0.015, 0.052], [0, 0.01, 0], [0, 0, 0], stripe);
      addPart(this.entity, "cylinder", "flash-neck", [0.02, 0.015, 0.02], [0, 0.04, 0], [0, 0, 0], steel);
      addPart(this.entity, "cylinder", "flash-indent1", [0.051, 0.005, 0.051], [0, -0.05, 0], [0, 0, 0], dark);
    } else if (color.r === 0.85 && color.g === 0.32) {
      // Molotov (bottle) — Stolichnaya-style olive bottle, red label, steel
      // band, lit rag wick. Built from the single shared source of truth
      // (player/molotovDesign.ts) so the local thrown bottle is identical to
      // the in-hand and peer in-flight models. NOTE: this path is currently
      // unused (Sprint 7 moved throw rendering to the server-driven
      // PeerGrenadeRenderer); kept building from the shared model for safety.
      buildMolotov(this.entity, addPart, makeMat);
    } else {
      // HE
      addPart(this.entity, "sphere", "he-body", [0.07, 0.09, 0.07], [0, -0.02, 0], [0, 0, 0], mat);
      for (let i = 0; i < 4; i++) {
        addPart(this.entity, "box", `he-rib-v${i}`, [0.075, 0.08, 0.01], [0, -0.02, 0], [0, i * 45, 0], dark);
      }
      addPart(this.entity, "box", "he-rib-h1", [0.075, 0.01, 0.075], [0, -0.04, 0], [0, 0, 0], dark);
      addPart(this.entity, "cylinder", "he-neck", [0.025, 0.02, 0.025], [0, 0.035, 0], [0, 0, 0], steel);
      const stripe = new pc.StandardMaterial(); stripe.diffuse = new pc.Color(0.8, 0.8, 0.1); stripe.update();
      addPart(this.entity, "cylinder", "he-stripe", [0.026, 0.005, 0.026], [0, 0.04, 0], [0, 0, 0], stripe);
    }

    this.entity.setPosition(spec.origin);
    this.app.root.addChild(this.entity);

    this.tickFn = (dt: number) => this.tick(dt);
    this.app.on("update", this.tickFn);
  }

  private tick(dt: number): void {
    if (this.done) return;
    this.age += dt;

    // Integrate.
    this.velocity.y -= GRAVITY * dt;
    const pos = this.entity.getPosition();
    let nx = pos.x + this.velocity.x * dt;
    let ny = pos.y + this.velocity.y * dt;
    let nz = pos.z + this.velocity.z * dt;

    // Floor collision. Detonate-on-floor (molotov) ends here.
    const RADIUS = 0.07;
    if (ny <= FLOOR + RADIUS) {
      ny = FLOOR + RADIUS;
      if (this.spec.detonateOnFloor) {
        this.entity.setPosition(nx, ny, nz);
        return this.finish();
      }
      // Bounce with damping.
      if (this.velocity.y < -0.4) {
        this.velocity.y = -this.velocity.y * 0.35;
        this.velocity.x *= 0.6;
        this.velocity.z *= 0.6;
      } else {
        // Settle.
        this.velocity.set(0, 0, 0);
        this.settled = true;
      }
    }
    this.entity.setPosition(nx, ny, nz);

    // Tumble for visual flavor.
    if (!this.settled) {
      const e = this.entity.getLocalEulerAngles();
      this.entity.setLocalEulerAngles(e.x + dt * 540, e.y + dt * 360, e.z);
    }

    // Fuse check.
    if (this.age >= this.spec.fuseSec) return this.finish();
  }

  private finish(): void {
    if (this.done) return;
    this.done = true;
    this.app.off("update", this.tickFn);
    const finalPos = this.entity.getPosition().clone();
    // Defer destroy so onLand can read pos cleanly.
    queueMicrotask(() => {
      this.entity.destroy();
      this.onLand(finalPos);
    });
  }
}

/**
 * Compute a polyline of (x, y, z) points sampling the same physics as
 * the server-authoritative `GrenadeSystem`, used for the green
 * trajectory preview while the player is charging a throw.
 *
 * Sprint 7.1 — uses the shared `getArenaAABBs()` + `rayHitAabb` so the
 * preview respects the walls a grenade would actually hit (instead of
 * clipping through pillars / crates).
 *
 * Wall hits now REFLECT (mirroring the server's bounce: full reflect ×
 * 0.35 damp, ×0.6 lateral friction) for up to `wallBounces` contacts —
 * every grenade kind banks off vertical surfaces server-side (including
 * the molotov, CS-style), so the line shows where a bank shot actually
 * ends up. The line terminates on floor-like contact: the world floor or
 * a box TOP face (where a molotov ignites / others first land).
 * Returns ≤ maxSteps points.
 */
export function predictTrajectory(
  origin: pc.Vec3,
  velocity: pc.Vec3,
  maxSteps = 64,
  stepDt = 0.04,
  wallBounces = 0,
): pc.Vec3[] {
  const out: pc.Vec3[] = [];
  let x = origin.x, y = origin.y, z = origin.z;
  let vx = velocity.x, vy = velocity.y, vz = velocity.z;
  const FLOOR_Y = FLOOR + 0.07;
  // Mirror GrenadeSystem's bounce response so the previewed bank matches
  // the authoritative trajectory.
  const BOUNCE_DAMP = 0.35;
  const LATERAL_FRICTION = 0.6;
  const aabbs = getArenaAABBs();
  let bouncesLeft = wallBounces;

  for (let i = 0; i < maxSteps; i++) {
    out.push(new pc.Vec3(x, y, z));
    vy -= GRAVITY * stepDt;
    const nx = x + vx * stepDt;
    const ny = y + vy * stepDt;
    const nz = z + vz * stepDt;

    // Floor — terminal: molotov ignites here, everything else lands.
    if (ny <= FLOOR_Y) {
      const t = (FLOOR_Y - y) / Math.max(1e-6, ny - y);
      out.push(new pc.Vec3(x + (nx - x) * t, FLOOR_Y, z + (nz - z) * t));
      break;
    }

    // Wall AABB — sweep the segment for a hit.
    const dx = nx - x, dy = ny - y, dz = nz - z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (dist > 1e-6) {
      const dirX = dx / dist, dirY = dy / dist, dirZ = dz / dist;
      const hit = rayHitAabb(x, y, z, dirX, dirY, dirZ, dist + 0.07, aabbs);
      if (hit) {
        const stopT = Math.max(0, hit.t - 0.05);
        const cx = x + dirX * stopT;
        const cy = y + dirY * stopT;
        const cz = z + dirZ * stopT;
        out.push(new pc.Vec3(cx, cy, cz));
        // Top face = floor-like contact → terminal (ignition/landing).
        if (hit.ny > 0.7 || bouncesLeft <= 0) break;
        bouncesLeft--;
        // Reflect off the face + damp, exactly like the server bounce.
        const vDotN = vx * hit.nx + vy * hit.ny + vz * hit.nz;
        vx -= 2 * vDotN * hit.nx;
        vy -= 2 * vDotN * hit.ny;
        vz -= 2 * vDotN * hit.nz;
        vx *= BOUNCE_DAMP; vy *= BOUNCE_DAMP; vz *= BOUNCE_DAMP;
        vx *= LATERAL_FRICTION; vz *= LATERAL_FRICTION;
        x = cx; y = cy; z = cz;
        continue;
      }
    }

    x = nx; y = ny; z = nz;
  }
  return out;
}
